import type { MembershipAnalytics } from '../api/membership.api';
import Skeleton from '../../../shared/components/ui/Skeleton';

type MembershipAnalyticsStripProps = {
  analytics?: MembershipAnalytics;
  loading?: boolean;
  error?: string;
};

const formatRupeesFromPaise = (value: number): string => `₹${(Math.max(value || 0, 0) / 100).toLocaleString('en-IN')}`;
const formatRupeesCompact = (value: number): string => `₹${Math.round(Math.max(value || 0, 0)).toLocaleString('en-IN')}`;

type KpiConfig = {
  label: string;
  value: string;
  sub?: string;
  icon: string;
  trend?: { direction: 'up' | 'down' | 'neutral'; label: string };
};

const card = ({ label, value, sub, icon, trend }: KpiConfig) => (
  <article className="appointments-v2-kpi-card" key={label}>
    <div className="appointments-v2-kpi-top">
      <span className="appointments-v2-kpi-icon" aria-hidden="true">{icon}</span>
      <p className="appointments-v2-kpi-label">{label}</p>
    </div>
    <p className="appointments-v2-kpi-value">{value}</p>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {trend ? (
        <span className={`appointments-v2-kpi-trend is-${trend.direction}`}>
          {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '—'} {trend.label}
        </span>
      ) : null}
      {sub ? <p className="appointments-v2-kpi-footnote">{sub}</p> : null}
    </div>
  </article>
);

const MembershipAnalyticsStrip = ({ analytics, loading, error = '' }: MembershipAnalyticsStripProps) => {
  if (loading) {
    return (
      <section className="membership-kpi-strip" aria-label="Loading membership analytics">
        {Array.from({ length: 4 }).map((_, i) => (
          <article className="appointments-v2-kpi-card" key={`membership-kpi-${i}`}>
            <div className="appointments-v2-kpi-top">
              <Skeleton height={28} width="28px" />
              <Skeleton height={12} width="45%" />
            </div>
            <Skeleton height={30} width="60%" />
            <Skeleton height={12} width="50%" />
          </article>
        ))}
      </section>
    );
  }

  if (error) {
    return (
      <article className="appointments-v2-card">
        <div className="appointments-v2-card-body">
          <p className="appointments-v2-empty-copy">{error}</p>
        </div>
      </article>
    );
  }

  if (!analytics) {
    return (
      <article className="appointments-v2-card">
        <div className="appointments-v2-card-body">
          <p className="appointments-v2-empty-copy">Membership analytics will appear here once the summary service responds.</p>
        </div>
      </article>
    );
  }

  const totalMembers = analytics.activeCount || 0;
  const averageLtv = totalMembers > 0
    ? formatRupeesCompact(Math.round((analytics.revenuePaise || 0) / 100 / totalMembers))
    : '—';

  const kpis: KpiConfig[] = [
    {
      label: 'TOTAL MEMBERS',
      value: String(totalMembers),
      icon: '👥',
      trend: { direction: totalMembers > 0 ? 'up' : 'neutral', label: totalMembers > 0 ? 'active' : 'none yet' },
    },
    {
      label: 'TIER REVENUE (30D)',
      value: formatRupeesFromPaise(analytics.revenuePaise || 0),
      icon: '₹',
      trend: { direction: (analytics.revenuePaise || 0) > 0 ? 'up' : 'neutral', label: (analytics.revenuePaise || 0) > 0 ? 'earning' : 'no revenue' },
    },
    {
      label: 'AVG MEMBER LTV',
      value: averageLtv,
      sub: totalMembers > 0 ? 'derived from current tier revenue' : undefined,
      icon: '📊',
      trend: { direction: totalMembers > 0 ? 'neutral' : 'neutral', label: totalMembers > 0 ? 'calculated' : 'n/a' },
    },
    {
      label: 'RETENTION RATE',
      value: '—',
      sub: 'data not yet available',
      icon: '🔄',
      trend: { direction: 'neutral', label: 'pending' },
    },
  ];

  return (
    <section className="membership-kpi-strip" aria-label="Membership analytics">
      {kpis.map((kpi) => card(kpi))}
    </section>
  );
};

export default MembershipAnalyticsStrip;
